import { useCallback, useEffect, useRef, useState } from "react";

/*
 * Master Stage 9 (4.4) — shared admin load-state hook.
 * Wraps a promise-returning loader and guarantees a page never sits on
 * "Loading…" forever: after `timeoutMs` (default 15s) or on error it moves to
 * the "error" state so the caller can render a role="alert" + Retry.
 *
 * Returns { state, data, error, reload } where state ∈
 *   "loading" | "ready" | "error".
 * Emptiness ("No {things} match…") is left to the caller to derive from `data`.
 */
export function useLoadState(loader, { timeoutMs = 15000, deps = [] } = {}) {
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const aliveRef = useRef(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const run = useCallback(() => {
    aliveRef.current = true;
    setState("loading");
    setError(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (aliveRef.current) {
        setError(new Error("Request timed out after 15 seconds."));
        setState("error");
      }
    }, timeoutMs);

    Promise.resolve()
      .then(() => loaderRef.current())
      .then((result) => {
        if (!aliveRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        setData(result);
        setState("ready");
      })
      .catch((e) => {
        if (!aliveRef.current) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        setError(e instanceof Error ? e : new Error(String(e?.message || e || "Failed to load")));
        setState("error");
      });
  }, [timeoutMs]);

  useEffect(() => {
    run();
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { state, data, error, reload: run };
}

export default useLoadState;

// Minimal 15s timeout wrapper for screens that keep their own state/setters
// (so adding a load timeout + Retry doesn't require a full state rewrite).
export function withTimeout(promise, ms = 15000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out after 15 seconds.")), ms)),
  ]);
}
